// components/LockStatusPanel.js
//
// Per-property lock investigation surface, mounted as the admin-only
// "Locks" tab on SecurePropertyDetails. Pure presentation — all business
// logic (suggested-code, booking match) lives server-side
// in /api/v1/locks/property/:propertyId.

import React, { useEffect, useState, useCallback, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import lockApi from '../services/lockApi';
import useLanguage from '../hooks/useLanguage';
import { formatDateTime } from '../utils/dateFormatter';
import { formatAgeLabel, hoursSince } from '../utils/alertDisplay';
import { batteryColor } from '../utils/lockUiHelpers';
import LockMappingModal from './LockMappingModal';

// ─── Helpers ──────────────────────────────────────────────────────────────

const isPermanent = (passcode) => {
  // TTLock sentinel: endDate=0 means "no expiry". After our tsToDate
  // conversion it becomes null.
  return !passcode.endDate;
};

const passcodeLifecycleState = (passcode, now = new Date()) => {
  // TTLock passcode status: 1 = Normal (on the lock), 4 = Adding (cloud-queued,
  // not yet on the lock). A cloud-queued code is being delivered, NOT expired —
  // label it "syncing" (L48 lifecycle, matches the booking-page قيد المزامنة badge).
  if (passcode.status === 4) return 'syncing';
  // status: 0 → disabled, other non-1 → not on the lock
  if (passcode.status !== 1) return 'expired';
  const start = passcode.startDate ? new Date(passcode.startDate) : null;
  const end = passcode.endDate ? new Date(passcode.endDate) : null;
  if (start && now < start) return 'not_yet_active';
  if (end && now >= end) return 'expired';
  return 'active';
};

const codeBadgeKey = (passcode) => {
  if (passcode.matchedBookingId) return 'matchedGuest';
  if (passcode.parsedBookingRef) return 'staffOrUnknown'; // parsed but didn't match
  if (passcode.parsedGuestName === null && passcode.parsedBookingRef === null) {
    return 'staffOrUnknown'; // doesn't follow naming format
  }
  return 'nonConforming';
};

const recordTypeKey = (recordType) => {
  switch (recordType) {
    case 4: return 'passcode';
    case 8: return 'fingerprint';
    case 48: return 'bluetooth';
    case 7: return 'icCard';
    case 1: return 'remoteApp';
    default: return 'unknown';
  }
};

// ─── Component ────────────────────────────────────────────────────────────

const LockStatusPanel = ({ propertyId }) => {
  const { t, language } = useLanguage();
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [confirmingUnmap, setConfirmingUnmap] = useState(false);
  const [unmapping, setUnmapping] = useState(false);
  const [unmapError, setUnmapError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await lockApi.getPropertyLockState(propertyId);
      setData(response);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUnmap = async () => {
    if (!data?.lock?._id) return;
    setUnmapping(true);
    setUnmapError(null);
    try {
      await lockApi.deactivateLock(data.lock._id);
      setConfirmingUnmap(false);
      await fetchData();
    } catch (err) {
      setUnmapError(err.response?.data?.message || err.message);
    } finally {
      setUnmapping(false);
    }
  };

  // ─── Render states ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('locks.panel.loading')}</span>
        </div>
        <p className="mt-2 text-muted">{t('locks.panel.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h5 className="alert-heading">{t('locks.panel.error.title')}</h5>
        <p className="mb-2">{error}</p>
        <button className="btn btn-sm btn-outline-danger" onClick={fetchData}>
          {t('locks.panel.error.retry')}
        </button>
      </div>
    );
  }

  if (!data?.lock) {
    return (
      <div dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <div className="alert alert-info d-flex justify-content-between align-items-center flex-wrap gap-2" role="alert">
          <span>
            <i className="bi bi-info-circle me-2"></i>
            {t('locks.panel.noLockMapped')}
          </span>
          {user?.role === 'admin' && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setShowMappingModal(true)}
            >
              <i className="bi bi-link-45deg me-1"></i>
              {t('locks.panel.mapButton')}
            </button>
          )}
        </div>
        {showMappingModal && (
          <LockMappingModal
            propertyId={propertyId}
            onClose={() => setShowMappingModal(false)}
            onMapped={fetchData}
          />
        )}
      </div>
    );
  }

  // suggestedCode intentionally NOT destructured — the property page never shows
  // a guest code (L46: booking is the sole home, credential-leak boundary).
  const { lock, recentEvents = [] } = data;
  const now = new Date();

  // ─── Header card ────────────────────────────────────────────────────────

  const renderHeader = () => (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <h4 className="mb-1">
              {lock.ttlock?.lockAlias || lock.ttlock?.lockName}
              {user?.role === 'admin' && !confirmingUnmap && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger ms-2"
                  onClick={() => { setConfirmingUnmap(true); setUnmapError(null); }}
                  style={{ fontSize: '0.75rem', verticalAlign: 'middle' }}
                >
                  <i className="bi bi-link-45deg me-1"></i>
                  {t('locks.panel.unmapButton')}
                </button>
              )}
            </h4>
            <small className="text-muted">
              ID: {lock.ttlock?.lockId}
              {lock.ttlock?.lockMac && ` · MAC: ${lock.ttlock.lockMac}`}
            </small>
          </div>
          <div className="d-flex gap-3 flex-wrap">
            <div className="text-center">
              <div style={{ color: batteryColor(lock.battery?.electricQuantity), fontSize: '1.5rem' }}>
                <i className="bi bi-battery-half"></i>
              </div>
              <div style={{ fontSize: '0.85rem' }}>
                <strong>
                  {lock.battery?.electricQuantity != null
                    ? `${lock.battery.electricQuantity}%`
                    : t('locks.panel.battery.unknown')}
                </strong>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  {t('locks.panel.battery.lastReported')}:{' '}
                  {lock.battery?.electricQuantityUpdateDate
                    ? formatDateTime(lock.battery.electricQuantityUpdateDate)
                    : '—'}
                </div>
              </div>
            </div>
            <div className="text-center">
              <div style={{
                color: lock.status?.isOnline ? '#28a745' : '#6c757d',
                fontSize: '1.5rem'
              }}>
                <i className={`bi bi-${lock.status?.isOnline ? 'wifi' : 'wifi-off'}`}></i>
              </div>
              <div style={{ fontSize: '0.85rem' }}>
                <strong>{lock.status?.isOnline ? t('locks.panel.online') : t('locks.panel.offline')}</strong>
              </div>
              {/* How long, not just whether — see AdminDashboardLockTable. */}
              {!lock.status?.isOnline && lock.status?.unreachableSince && (
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  {formatAgeLabel(hoursSince(lock.status.unreachableSince))}
                </div>
              )}
            </div>
          </div>
        </div>
        <hr className="my-3" />
        <div className="row" style={{ fontSize: '0.85rem' }}>
          <div className="col-md-6">
            <strong>{t('locks.panel.lastSync.passcodes')}: </strong>
            {lock.syncState?.passcodes?.lastSyncedAt
              ? formatDateTime(lock.syncState.passcodes.lastSyncedAt)
              : t('locks.panel.lastSync.never')}
          </div>
          <div className="col-md-6">
            <strong>{t('locks.panel.lastSync.events')}: </strong>
            {lock.syncState?.events?.lastSyncedAt
              ? formatDateTime(lock.syncState.events.lastSyncedAt)
              : t('locks.panel.lastSync.never')}
          </div>
        </div>
      </div>
    </div>
  );

  // ─── Codes list ─────────────────────────────────────────────────────────

  const renderCodesList = () => {
    const codes = lock.passcodes || [];
    if (codes.length === 0) {
      return (
        <div className="card mb-3">
          <div className="card-body">
            <h5 className="card-title">{t('locks.panel.codes.heading')}</h5>
            <p className="text-muted mb-0">{t('locks.panel.codes.emptyState')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title mb-3">
            {t('locks.panel.codes.heading')} ({codes.length})
          </h5>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>{t('locks.panel.codes.heading')}</th>
                  <th>—</th>
                  <th>{t('locks.panel.codes.validUntil')}</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((p) => {
                  const lifecycle = passcodeLifecycleState(p, now);
                  const badge = codeBadgeKey(p);
                  return (
                    <tr key={p.keyboardPwdId}>
                      <td>
                        <div style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          {p.keyboardPwd}
                        </div>
                        <small className="text-muted">{p.keyboardPwdName || '—'}</small>
                      </td>
                      <td>
                        <span className={`badge ${
                          badge === 'matchedGuest' ? 'bg-success'
                          : badge === 'nonConforming' ? 'bg-warning text-dark'
                          : 'bg-secondary'
                        }`}>
                          {t(`locks.panel.codes.${badge}`)}
                        </span>
                        {' '}
                        {lifecycle === 'syncing' && (
                          <span className="badge bg-info ms-1">
                            {t('locks.panel.codes.syncing')}
                          </span>
                        )}
                        {lifecycle === 'expired' && (
                          <span className="badge bg-danger ms-1">
                            {t('locks.panel.codes.expired')}
                          </span>
                        )}
                        {lifecycle === 'not_yet_active' && (
                          <span className="badge bg-info ms-1">
                            {t('locks.panel.codes.notYetActive')}
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>
                        {isPermanent(p)
                          ? t('locks.panel.codes.permanent')
                          : formatDateTime(p.endDate)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ─── Recent events ──────────────────────────────────────────────────────

  const renderEvents = () => {
    if (recentEvents.length === 0) {
      return (
        <div className="card mb-3">
          <div className="card-body">
            <h5 className="card-title">{t('locks.panel.events.heading')}</h5>
            <p className="text-muted mb-0">{t('locks.panel.events.emptyState')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title mb-3">{t('locks.panel.events.heading')}</h5>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <tbody>
                {recentEvents.map((e) => (
                  <tr key={e._id}>
                    <td style={{ width: '180px', fontSize: '0.85rem' }}>
                      {formatDateTime(e.lockDate)}
                    </td>
                    <td>
                      <span className="badge bg-light text-dark">
                        {t(`locks.panel.events.recordType.${recordTypeKey(e.recordType)}`)}
                      </span>
                      {' '}
                      {e.username && (
                        <span className="ms-2">{e.username}</span>
                      )}
                    </td>
                    <td style={{ width: '90px' }}>
                      {e.success ? (
                        <span className="badge bg-success">{t('locks.panel.events.success')}</span>
                      ) : (
                        <span className="badge bg-danger">{t('locks.panel.events.failed')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };


  return (
    <div dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {confirmingUnmap && (
        <div className="alert alert-warning mb-3" role="alert">
          <p className="mb-2">{t('locks.panel.unmapConfirm.message')}</p>
          {unmapError && (
            <p className="mb-2 text-danger">
              <strong>{t('locks.panel.unmapError')}:</strong> {unmapError}
            </p>
          )}
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={handleUnmap}
              disabled={unmapping}
            >
              {unmapping ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {t('locks.panel.unmapping')}
                </>
              ) : (
                t('locks.panel.unmapConfirm.confirm')
              )}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => { setConfirmingUnmap(false); setUnmapError(null); }}
              disabled={unmapping}
            >
              {t('locks.panel.unmapConfirm.cancel')}
            </button>
          </div>
        </div>
      )}
      {renderHeader()}
      {renderCodesList()}
      {renderEvents()}
    </div>
  );
};

export default LockStatusPanel;
